import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react'
import { useServiceAuth } from '@/contexts/AuthContext'
import type { TimeShiftConfig } from '@/interfaces/models/TimeShift'
import type { ToolType } from '@/interfaces/toolData'
import { TimeShiftService } from '@/services/timeShiftService'

// =============================================================================
// Types
// =============================================================================

interface TimeShiftState {
  projectId: string | null
  timeShiftConfig: TimeShiftConfig | null
  isLoading: boolean
  error: string | null
}

// =============================================================================
// Actions
// =============================================================================

type TimeShiftAction =
  | { type: 'SET_PROJECT_ID'; payload: string }
  | { type: 'SET_TIME_SHIFT_CONFIG'; payload: TimeShiftConfig }
  | { type: 'UPDATE_TIME_SHIFT'; payload: { deviceType: ToolType; offsetMs: number; deviceIdentifier?: string } }
  | { type: 'REMOVE_TIME_SHIFT'; payload: { deviceType: ToolType; deviceIdentifier?: string } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }

// =============================================================================
// Reducer
// =============================================================================

const initialState: TimeShiftState = {
  projectId: null,
  timeShiftConfig: null,
  isLoading: false,
  error: null
}

function timeShiftReducer(state: TimeShiftState, action: TimeShiftAction): TimeShiftState {
  switch (action.type) {
    case 'SET_PROJECT_ID':
      return { ...state, projectId: action.payload }
    
    case 'SET_TIME_SHIFT_CONFIG':
      return { ...state, timeShiftConfig: action.payload }
    
    case 'UPDATE_TIME_SHIFT': {
      const { deviceType, offsetMs, deviceIdentifier } = action.payload
      const key = deviceType // Use deviceType as key for device-level shifts
      
      const newShifts = new Map(state.timeShiftConfig?.shifts || new Map())
      
      if (offsetMs !== 0) {
        newShifts.set(key, {
          projectId: state.projectId!,
          deviceType,
          deviceIdentifier,
          timeOffsetMs: offsetMs,
          userId: 'unknown', // Will be set by service
          organizationId: 'unknown' // Will be set by service
        })
      } else {
        newShifts.delete(key)
      }
      
      return {
        ...state,
        timeShiftConfig: {
          projectId: state.projectId!,
          shifts: newShifts
        }
      }
    }
    
    case 'REMOVE_TIME_SHIFT': {
      const { deviceType } = action.payload
      const key = deviceType
      
      const newShifts = new Map(state.timeShiftConfig?.shifts || new Map())
      newShifts.delete(key)
      
      return {
        ...state,
        timeShiftConfig: {
          projectId: state.projectId!,
          shifts: newShifts
        }
      }
    }
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    
    default:
      return state
  }
}

// =============================================================================
// Context
// =============================================================================

interface TimeShiftContextValue {
  // State
  timeShiftConfig: TimeShiftConfig | null
  isLoading: boolean
  error: string | null
  
  // Actions
  loadTimeShifts: (projectId: string) => Promise<void>
  updateTimeShift: (deviceType: ToolType, offsetMs: number, deviceIdentifier?: string) => Promise<void>
  clearTimeShift: (deviceType: ToolType, deviceIdentifier?: string) => Promise<void>
}

const TimeShiftContext = createContext<TimeShiftContextValue | null>(null)

// =============================================================================
// Hook
// =============================================================================

export function useTimeShifts() {
  const context = useContext(TimeShiftContext)
  if (!context) {
    throw new Error('useTimeShifts must be used within a TimeShiftProvider')
  }
  return context
}

// =============================================================================
// Provider
// =============================================================================

interface TimeShiftProviderProps {
  children: React.ReactNode
  projectId?: string
}

export function TimeShiftProvider({ 
  children, 
  projectId: propProjectId
}: TimeShiftProviderProps) {
  const [state, dispatch] = useReducer(timeShiftReducer, initialState)
  const getServiceAuth = useServiceAuth()

  // Get TimeShift service instance - create fresh service when auth ready
  const getTimeShiftService = useCallback(() => {
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) {
      return null
    }
    
    return new TimeShiftService({
      userId: serviceAuth.userId,
      orgId: serviceAuth.orgId,
      orgSlug: '', // TimeShiftService doesn't need orgSlug
      getToken: serviceAuth.getToken
    })
  }, [getServiceAuth])

  // Update project ID when prop changes
  useEffect(() => {
    if (propProjectId && propProjectId !== state.projectId) {
      dispatch({ type: 'SET_PROJECT_ID', payload: propProjectId })
      loadTimeShifts(propProjectId)
    }
  }, [propProjectId])

  // Load time shifts from database
  const loadTimeShifts = useCallback(async (projectId: string) => {
    const timeShiftService = getTimeShiftService()
    if (!timeShiftService) return

    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })

    try {
      console.log('🔄 Loading time shifts from database for project:', projectId)
      const config = await timeShiftService.getTimeShifts(projectId)
      
      console.log('✅ Time shifts loaded:', config.shifts.size, 'shifts')
      dispatch({ type: 'SET_TIME_SHIFT_CONFIG', payload: config })
    } catch (error) {
      console.error('❌ Failed to load time shifts:', error)
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to load time shifts' 
      })
      
      // Initialize empty config on error
      dispatch({ 
        type: 'SET_TIME_SHIFT_CONFIG', 
        payload: { projectId, shifts: new Map() } 
      })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [getTimeShiftService])

  // Update time shift
  const updateTimeShift = useCallback(async (
    deviceType: ToolType, 
    offsetMs: number, 
    deviceIdentifier?: string
  ) => {
    const timeShiftService = getTimeShiftService()
    if (!timeShiftService || !state.projectId) {
      console.warn('⚠️ No service or project ID available for time shift save')
      return
    }

    console.log(`🕒 Time shift changed for ${deviceType}:`, { offsetMs, deviceIdentifier })
    
    try {
      // Update local state immediately for responsive UI
      dispatch({ 
        type: 'UPDATE_TIME_SHIFT', 
        payload: { deviceType, offsetMs, deviceIdentifier } 
      })
      
      // Save to database or delete if offset is zero
      if (offsetMs === 0) {
        console.log(`🗑️ Deleting time shift for ${deviceType}`)
        await timeShiftService.deleteTimeShift(state.projectId, deviceType)
      } else {
        await timeShiftService.saveTimeShift(state.projectId, deviceType, offsetMs)
      }
      
      console.log(`✅ Time shift save successful for ${deviceType}`)
    } catch (error) {
      console.error('❌ Failed to save time shift:', error)
      
      // Reload from database on error to ensure consistency
      await loadTimeShifts(state.projectId)
      
      throw error
    }
  }, [getTimeShiftService, state.projectId, loadTimeShifts])

  // Clear time shift (convenience method)
  const clearTimeShift = useCallback(async (
    deviceType: ToolType, 
    deviceIdentifier?: string
  ) => {
    await updateTimeShift(deviceType, 0, deviceIdentifier)
  }, [updateTimeShift])

  const contextValue: TimeShiftContextValue = {
    timeShiftConfig: state.timeShiftConfig,
    isLoading: state.isLoading,
    error: state.error,
    loadTimeShifts,
    updateTimeShift,
    clearTimeShift
  }

  return (
    <TimeShiftContext.Provider value={contextValue}>
      {children}
    </TimeShiftContext.Provider>
  )
}